export interface JustifiedGalleryController {
  update: () => void;
  destroy: () => void;
}

export function justifiedGallery(container: HTMLElement, options = {}): JustifiedGalleryController {
  const cfg = {
    margins: 2,
    rowHeight: 165,
    lastRow: 'nojustify',
    selector: '.comp-image',
    ...options
  };

  const layout = () => {
    const items = [...container?.querySelectorAll<HTMLElement>(cfg.selector)]
      .filter(el => el.offsetParent !== null); // :visible

    if (!items.length) return;

    const containerWidth = container.offsetWidth;

    // Collect aspect ratios
    const ratios = items.map(el => {
       const img = el.querySelector<HTMLImageElement>('img');
       const w = (img ? img.naturalWidth : el.offsetWidth) || 1;
       const h = (img ? img.naturalHeight : el.offsetHeight) || 1;
      return w / h;
    });

    // Build rows
    const rows: number[][] = [];
    let currentRow: number[] = [];
    let currentWidth = 0;

    ratios.forEach((ratio, i) => {
      const itemWidth = cfg.rowHeight * ratio;
      const spacingUsed = (currentRow.length) * cfg.margins;

      if (currentWidth + itemWidth + spacingUsed > containerWidth && currentRow.length > 0) {
        rows.push([...currentRow]);
        currentRow = [i];
        currentWidth = itemWidth;
      } else {
        currentRow.push(i);
        currentWidth += itemWidth;
      }
    });

    if (currentRow.length) rows.push(currentRow);

    // Pre-compute justified height for every row
    const rowHeights = rows.map(row => {
      const totalSpacing = (row.length - 1) * cfg.margins;
      const totalRatio = row.reduce((sum, i) => sum + ratios[i], 0);
      return (containerWidth - totalSpacing) / totalRatio;
    });

    // For nojustify last row: match the preceding row height so all rows look
    // visually consistent (avoids the last row appearing smaller than the rest)
    if (cfg.lastRow === 'nojustify' && rows.length > 0) {
      const last = rows.length - 1;
      rowHeights[last] = last > 0 ? rowHeights[last - 1] : cfg.rowHeight;
    }

    // Position items
    container.style.position = 'relative';
    let top = 0;

    rows.forEach((row, rowIndex) => {
      const rowHeight = rowHeights[rowIndex];

      let left = 0;
      row.forEach((itemIndex) => {
        const el = items[itemIndex];
        const w = Math.round(rowHeight * ratios[itemIndex]);
        const h = Math.round(rowHeight);

        Object.assign(el.style, {
          position: 'absolute',
          top: top + 'px',
          left: left + 'px',
          width: w + 'px',
          height: h + 'px',
          margin: '0',
          boxSizing: 'border-box',
        });

        left += w + cfg.margins;
      });

      top += Math.round(rowHeight) + cfg.margins;
    });

    container.style.height = (top - cfg.margins) + 'px';
  };

  // Initial layout
  layout();

  // Re-layout automatically when the container width changes
  let lastWidth = container.offsetWidth;
  const observer = new ResizeObserver(() => {
    const newWidth = container.offsetWidth;
    if (newWidth !== lastWidth) {
      lastWidth = newWidth;
      requestAnimationFrame(layout);
    }
  });
  observer.observe(container);

  return {
    update: layout,
    destroy: () => observer.disconnect(),
  };
}

